import { useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { InfoTip } from "@/components/app/Common";
import { cn } from "@/lib/utils";

const MAX_FILES = 500;
const OK = /\.(pdf|docx|doc|zip)$/i;
const CHUNK_FILES = 10;
const CHUNK_BYTES = 8 * 1024 * 1024;

function chunk(files) {
  const out = [];
  let cur = [], size = 0;
  for (const f of files) {
    if (cur.length && (cur.length >= CHUNK_FILES || size + f.size > CHUNK_BYTES)) { out.push(cur); cur = []; size = 0; }
    cur.push(f); size += f.size;
  }
  if (cur.length) out.push(cur);
  return out;
}

export function BulkUpload({ jobId, onStarted, onClose }) {
  const [files, setFiles] = useState([]);
  const [csv, setCsv] = useState(null);
  const [drag, setDrag] = useState(false);
  const [sending, setSending] = useState(null);
  const input = useRef(null);

  const add = (list) => {
    const incoming = Array.from(list);
    const bad = incoming.filter((f) => !OK.test(f.name));
    if (bad.length) toast.error(`${bad.length} file(s) skipped: use PDF, DOCX, DOC or ZIP`);
    setFiles((prev) => [...prev, ...incoming.filter((f) => OK.test(f.name))].slice(0, MAX_FILES));
  };

  const start = async () => {
    try {
      const form = new FormData();
      form.append("expected_total", String(files.length));
      if (csv) form.append("metadata_csv", csv);
      const { data } = await api.post(`/jobs/${jobId}/batches`, form);
      onStarted(data.batch_id);
      toast.success(`Uploading ${files.length} file(s). Parsing starts right away.`);
      const parts = chunk(files);
      for (let i = 0; i < parts.length; i++) {
        setSending({ done: i, total: parts.length });
        const fd = new FormData();
        parts[i].forEach((f) => fd.append("files", f));
        await api.post(`/batches/${data.batch_id}/files`, fd).catch((e) => { if (e.response?.status === 422) { toast.error(errMsg(e)); return; } throw e; });
      }
      setSending(null);
      setFiles([]);
      onClose();
    } catch (e) {
      setSending(null);
      toast.error(errMsg(e));
    }
  };

  return (
    <section className="panel space-y-4 p-6" data-testid="bulk-upload">
      <div className="flex items-center justify-between max-sm:flex-wrap max-sm:gap-2">
        <h3 className="text-base font-semibold">Add resumes</h3>
        <button onClick={onClose} aria-label="Close" className="text-ink-muted hover:text-ink" data-testid="bulk-upload-close"><X size={18} /></button>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        onClick={() => input.current?.click()}
        className={cn("flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[6px] border border-dashed p-8 text-center transition-colors duration-150",
          drag ? "border-brand bg-brand-tint" : "border-line bg-subtle hover:bg-white")}
        data-testid="bulk-dropzone"
      >
        <UploadCloud size={24} className="text-brand" />
        <p className="text-sm font-medium text-ink">Drop resumes here or click to choose</p>
        <p className="text-xs text-ink-muted">PDF, DOCX, DOC or a ZIP of resumes · up to {MAX_FILES} files · 10 MB each</p>
        <input ref={input} type="file" multiple accept=".pdf,.docx,.doc,.zip" className="hidden" data-testid="bulk-file-input"
          onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-brand hover:underline">
          <FileSpreadsheet size={16} /> {csv ? csv.name : "Add CSV metadata (optional)"}
          <input type="file" accept=".csv" className="hidden" onChange={(e) => setCsv(e.target.files?.[0] || null)} data-testid="bulk-csv-input" />
          <InfoTip text="A CSV with a 'filename' column plus any of name, email, phone, location, linkedin. Values override what we read from the resume." />
        </label>
        <div className="flex items-center gap-3">
          <span className="text-sm text-ink-secondary" data-testid="bulk-selected-count">{files.length} file(s) selected</span>
          {files.length > 0 && !sending && <Button variant="ghost" size="sm" onClick={() => setFiles([])} data-testid="bulk-clear-btn">Clear</Button>}
          <Button onClick={start} disabled={!files.length || !!sending} data-testid="bulk-upload-start-btn">
            {sending ? <><Loader2 size={16} className="animate-spin" /> Sending {sending.done + 1} of {sending.total}</> : "Upload and parse"}
          </Button>
        </div>
      </div>
    </section>
  );
}
